import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import ProductDetailModal from '../../components/ui/ProductDetailModal';
import ConsultationHeader from './components/ConsultationHeader';
import ProductResultsTable from './components/ProductResultsTable';
import ComparisonPanel from './components/ComparisonPanel';
import AuditTrailPanel from './components/AuditTrailPanel';
import NotesSection from './components/NotesSection';
import ApiMetadataPanel from './components/ApiMetadataPanel';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { ConsultationDetail, ProductResult, ConsultationNote } from './types';

const HistoricalConsultationDetail = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'results' | 'comparison' | 'audit' | 'api'>('results');
  const [selectedProduct, setSelectedProduct] = useState<ProductResult | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReExecuting, setIsReExecuting] = useState(false);
  const [consultation, setConsultation] = useState<ConsultationDetail | null>(null);

  useEffect(() => {
    const mockConsultation: ConsultationDetail = {
      id: 'CONS-2024-001234',
      consultationNumber: '2024-001234',
      status: 'success',
      createdAt: new Date('2024-01-15T10:30:00'),
      createdBy: 'Maria Silva',
      company: {
        cnpj: '12.345.678/0001-90',
        name: 'Empresa Exemplo Ltda',
        taxRegime: 'Lucro Real',
        ufOrigin: 'SP',
        ufDestination: 'RJ'
      },
      operation: {
        cfop: '5102',
        purpose: 'Venda de mercadoria adquirida ou recebida de terceiros',
        operationType: 'Venda Interestadual',
        taxCharacteristics: ['Substituição Tributária', 'Diferencial de Alíquota']
      },
      products: [
        {
          id: 'PROD-001',
          code: 'PROD-12345',
          description: 'Notebook Dell Inspiron 15 3000 Intel Core i5 8GB 256GB SSD',
          ncm: '8471.30.12',
          cest: '21.012.00',
          quantity: 10,
          unitValue: 3500.00,
          totalValue: 35000.00,
          taxCalculation: {
            icms: {
              rate: 18.00,
              base: 35000.00,
              amount: 6300.00,
              cst: '00',
              exemption: undefined
            },
            ipi: {
              rate: 5.00,
              base: 35000.00,
              amount: 1750.00,
              cst: '50',
              exemption: undefined
            },
            pis: {
              rate: 1.65,
              base: 35000.00,
              amount: 577.50,
              cst: '01',
              exemption: undefined
            },
            cofins: {
              rate: 7.60,
              base: 35000.00,
              amount: 2660.00,
              cst: '01',
              exemption: undefined
            },
            totalTax: 11287.50,
            effectiveRate: 32.25
          },
          taxGrades: [
            {
              uf: 'SP',
              cfop: '5102',
              cst: '00',
              rate: 18.00,
              exemptions: [],
              validFrom: new Date('2024-01-01'),
              validUntil: undefined
            },
            {
              uf: 'RJ',
              cfop: '5102',
              cst: '00',
              rate: 20.00,
              exemptions: [],
              validFrom: new Date('2024-01-01'),
              validUntil: undefined
            }
          ],
          regulatoryCitations: [
            'RICMS/SP - Decreto 45.490/2000, Artigo 54',
            'Convênio ICMS 52/2017 - Substituição Tributária',
            'Lei Complementar 87/1996 - Lei Kandir'
          ]
        },
        {
          id: 'PROD-002',
          code: 'PROD-67890',
          description: 'Mouse Logitech MX Master 3 Wireless',
          ncm: '8471.60.52',
          cest: '21.013.00',
          quantity: 50,
          unitValue: 450.00,
          totalValue: 22500.00,
          taxCalculation: {
            icms: {
              rate: 18.00,
              base: 22500.00,
              amount: 4050.00,
              cst: '00',
              exemption: undefined
            },
            ipi: {
              rate: 5.00,
              base: 22500.00,
              amount: 1125.00,
              cst: '50',
              exemption: undefined
            },
            pis: {
              rate: 1.65,
              base: 22500.00,
              amount: 371.25,
              cst: '01',
              exemption: undefined
            },
            cofins: {
              rate: 7.60,
              base: 22500.00,
              amount: 1710.00,
              cst: '01',
              exemption: undefined
            },
            totalTax: 7256.25,
            effectiveRate: 32.25
          },
          taxGrades: [
            {
              uf: 'SP',
              cfop: '5102',
              cst: '00',
              rate: 18.00,
              exemptions: [],
              validFrom: new Date('2024-01-01'),
              validUntil: undefined
            }
          ],
          regulatoryCitations: [
            'RICMS/SP - Decreto 45.490/2000, Artigo 54',
            'Convênio ICMS 52/2017'
          ]
        }
      ],
      apiMetadata: {
        requestId: 'REQ-2024-001234-ABC123',
        timestamp: new Date('2024-01-15T10:30:15'),
        responseTime: 1250,
        apiVersion: '3.0.1',
        environment: 'production',
        tokenUsed: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ'
      },
      auditTrail: [
        {
          id: 'AUDIT-001',
          timestamp: new Date('2024-01-15T10:30:00'),
          user: 'Maria Silva',
          action: 'Consulta criada',
          details: 'Nova consulta tributária iniciada com 2 produtos',
          ipAddress: '192.168.1.100'
        },
        {
          id: 'AUDIT-002',
          timestamp: new Date('2024-01-15T10:30:15'),
          user: 'Sistema',
          action: 'API consultada',
          details: 'Requisição enviada para Gestor Tributário 3.0',
          ipAddress: '192.168.1.100'
        },
        {
          id: 'AUDIT-003',
          timestamp: new Date('2024-01-15T10:30:16'),
          user: 'Sistema',
          action: 'Resultados processados',
          details: 'Cálculos tributários concluídos com sucesso',
          ipAddress: '192.168.1.100'
        },
        {
          id: 'AUDIT-004',
          timestamp: new Date('2024-01-15T14:20:00'),
          user: 'João Santos',
          action: 'Consulta visualizada',
          details: 'Detalhes da consulta acessados',
          ipAddress: '192.168.1.105'
        },
        {
          id: 'AUDIT-005',
          timestamp: new Date('2024-01-15T15:45:00'),
          user: 'Maria Silva',
          action: 'Resultados exportados',
          details: 'Relatório exportado em formato Excel',
          ipAddress: '192.168.1.100'
        }
      ],
      notes: [
        {
          id: 'NOTE-001',
          author: 'Maria Silva',
          content: 'Consulta realizada para validação de operação interestadual. Cliente solicitou confirmação das alíquotas aplicadas.',
          createdAt: new Date('2024-01-15T10:35:00'),
          isInternal: false
        },
        {
          id: 'NOTE-002',
          author: 'João Santos',
          content: 'Verificar possibilidade de aplicação de benefício fiscal para o produto PROD-12345. Aguardando retorno do departamento jurídico.',
          createdAt: new Date('2024-01-15T14:25:00'),
          isInternal: true
        }
      ]
    };

    setConsultation(mockConsultation);
  }, []);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.altKey) {
        switch (e.key) {
          case 'r':
            e.preventDefault();
            setActiveTab('results');
            break;
          case 'c':
            e.preventDefault();
            setActiveTab('comparison');
            break;
          case 'a':
            e.preventDefault();
            setActiveTab('audit');
            break;
          case 'i':
            e.preventDefault();
            setActiveTab('api');
            break;
          case 'e':
            e.preventDefault();
            handleExport();
            break;
          case 'b':
            e.preventDefault();
            navigate('/consultation-history');
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [navigate]);

  const handleProductSelect = (product: ProductResult) => {
    setSelectedProduct({
      id: product.id,
      name: product.description,
      code: product.code,
      category: 'Eletrônicos',
      taxRate: product.taxCalculation.effectiveRate,
      description: product.description,
      regulations: product.regulatoryCitations,
      exemptions: product.taxCalculation.icms.exemption ? [product.taxCalculation.icms.exemption] : [],
      calculationMethod: `Base de Cálculo: ${product.taxCalculation.icms.base.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}\nAlíquota ICMS: ${product.taxCalculation.icms.rate}%\nAlíquota IPI: ${product.taxCalculation.ipi.rate}%\nAlíquota PIS: ${product.taxCalculation.pis.rate}%\nAlíquota COFINS: ${product.taxCalculation.cofins.rate}%`
    });
    setIsModalOpen(true);
  };

  const handleReExecute = () => {
    setIsReExecuting(true);
    setTimeout(() => {
      setIsReExecuting(false);
    }, 3000);
  };

  const handleAddNote = (content: string, isInternal: boolean) => {
    if (!consultation) return;

    const newNote: ConsultationNote = {
      id: `NOTE-${Date.now()}`,
      author: 'Usuário Atual',
      content,
      createdAt: new Date(),
      isInternal
    };

    setConsultation({
      ...consultation,
      notes: [...consultation.notes, newNote]
    });
  };

  const handleExport = () => {
    console.log('Exportando consulta...');
  };

  if (!consultation) {
    return (
      <div className="flex h-screen bg-background">
        <Sidebar onCollapsedChange={() => {}} />
        <div className="flex-1 lg:ml-64">
          <Header title="Detalhes da Consulta" onUserMenuClick={() => {}} />
          <main className="pt-16 p-6">
            <div className="flex items-center justify-center h-[calc(100vh-8rem)]">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-muted-foreground">Carregando consulta...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'results' as const, label: 'Resultados', icon: 'FileText', shortcut: 'Alt+R' },
    { id: 'comparison' as const, label: 'Comparação', icon: 'GitCompare', shortcut: 'Alt+C' },
    { id: 'audit' as const, label: 'Auditoria', icon: 'Shield', shortcut: 'Alt+A' },
    { id: 'api' as const, label: 'API', icon: 'Database', shortcut: 'Alt+I' }
  ];

  return (
    <div className="flex h-screen bg-background">
      <Sidebar onCollapsedChange={() => {}} />
      
      <div className="flex-1 lg:ml-64 flex flex-col">
        <Header title="Detalhes da Consulta" onUserMenuClick={() => {}} />
        
        <main className="flex-1 overflow-y-auto pt-16">
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                iconName="ArrowLeft"
                iconPosition="left"
                onClick={() => navigate('/consultation-history')}
              >
                Voltar ao Histórico
              </Button>

              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  iconName="Download"
                  iconPosition="left"
                  onClick={handleExport}
                >
                  Exportar
                </Button>
                <Button
                  variant="default"
                  iconName="Copy"
                  iconPosition="left"
                >
                  Duplicar Consulta
                </Button>
              </div>
            </div>

            <ConsultationHeader consultation={consultation} />

            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <div className="border-b border-border">
                <nav className="flex overflow-x-auto" role="tablist">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      role="tab"
                      aria-selected={activeTab === tab.id}
                      aria-controls={`panel-${tab.id}`}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        flex items-center gap-2 px-6 py-4 border-b-2 transition-all duration-150 whitespace-nowrap
                        ${activeTab === tab.id
                          ? 'border-primary text-primary font-medium' 
                          : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                        }
                      `}
                      title={tab.shortcut}
                    >
                      <Icon name={tab.icon} size={18} color="currentColor" />
                      <span>{tab.label}</span>
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6">
                {activeTab === 'results' && (
                  <div id="panel-results" role="tabpanel" className="space-y-6">
                    <ProductResultsTable
                      products={consultation.products}
                      onProductSelect={handleProductSelect}
                    />
                  </div>
                )}

                {activeTab === 'comparison' && (
                  <div id="panel-comparison" role="tabpanel">
                    <ComparisonPanel
                      originalProducts={consultation.products}
                      onReExecute={handleReExecute}
                      isExecuting={isReExecuting}
                    />
                  </div>
                )}

                {activeTab === 'audit' && (
                  <div id="panel-audit" role="tabpanel" className="space-y-6">
                    <AuditTrailPanel auditTrail={consultation.auditTrail} />
                    <NotesSection
                      notes={consultation.notes}
                      onAddNote={handleAddNote}
                    />
                  </div>
                )}

                {activeTab === 'api' && (
                  <div id="panel-api" role="tabpanel">
                    <ApiMetadataPanel metadata={consultation.apiMetadata} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      <ProductDetailModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        product={selectedProduct}
      />
    </div>
  );
};

export default HistoricalConsultationDetail;